import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { MaskedInput } from '@/components/ui/masked-input';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare, 
  Mic, 
  Camera, 
  Bell, 
  CheckCircle2, 
  Loader2, 
  Smartphone,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { useWhatsAppSettings } from '@/hooks/useWhatsAppSettings';
import { useAuth } from '@/components/AuthProvider';

const ONBOARDING_KEY = 'obrim_whatsapp_onboarding_completed';

interface WhatsAppOnboardingModalProps {
  forceOpen?: boolean;
  onClose?: () => void;
}

export function WhatsAppOnboardingModal({ forceOpen, onClose }: WhatsAppOnboardingModalProps) {
  const { user } = useAuth();
  const { settings, loading, verifyPhone, saving } = useWhatsAppSettings();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<'intro' | 'phone' | 'success'>('intro');
  const [phoneInput, setPhoneInput] = useState('');

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      return;
    }

    // Check if user is new and hasn't completed onboarding
    if (user && !loading) {
      const completed = localStorage.getItem(`${ONBOARDING_KEY}_${user.id}`);
      
      // Show if: not completed AND phone not verified
      if (!completed && !settings.phone_verified) {
        // Small delay to not show immediately on page load
        const timer = setTimeout(() => setOpen(true), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, loading, settings.phone_verified, forceOpen]);

  const handleClose = () => {
    if (user) {
      localStorage.setItem(`${ONBOARDING_KEY}_${user.id}`, 'true');
    }
    setOpen(false);
    onClose?.();
  };

  const handleSkip = () => {
    handleClose();
  };

  const handleVerify = async () => {
    const cleanPhone = phoneInput.replace(/\D/g, '');
    if (cleanPhone.length < 10) return;

    const success = await verifyPhone(cleanPhone);
    if (success) {
      setStep('success');
      // Auto close after success
      setTimeout(() => handleClose(), 3000);
    }
  };

  const features = [
    {
      icon: Mic,
      title: 'Áudio → Diário',
      description: 'Grave um áudio e ele vira registro de obra automaticamente',
    },
    {
      icon: Camera,
      title: 'Foto → Lançamento',
      description: 'Tire foto da nota fiscal e ela é lançada no financeiro',
    },
    {
      icon: Bell,
      title: 'Briefing Matinal',
      description: 'Receba um resumo das obras e tarefas todo dia cedo',
    },
    {
      icon: MessageSquare,
      title: 'Comandos Rápidos',
      description: 'Digite "status" ou "tarefas" e receba informações na hora',
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-lg">
        {step === 'intro' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <MessageSquare className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-2xl">
                Ative seu Assistente de Bolso
              </DialogTitle>
              <DialogDescription className="text-base">
                Gerencie suas obras direto pelo WhatsApp. Sem precisar abrir o app!
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-3">
              {features.map((feature, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 rounded-lg border border-border/50 bg-muted/30 p-3"
                >
                  <div className="rounded-lg bg-primary/10 p-2">
                    <feature.icon className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{feature.title}</p>
                    <p className="text-xs text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex flex-col gap-2">
              <Button onClick={() => setStep('phone')} className="w-full gap-2">
                <Smartphone className="h-4 w-4" />
                Conectar meu WhatsApp
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button variant="ghost" onClick={handleSkip} className="text-muted-foreground">
                Talvez depois
              </Button>
            </div>
          </>
        )}

        {step === 'phone' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <Smartphone className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-xl">
                Digite seu número de WhatsApp
              </DialogTitle>
              <DialogDescription>
                Enviaremos uma mensagem de confirmação
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-4">
              <div className="flex gap-3">
                <div className="flex items-center gap-2 rounded-lg border bg-muted/50 px-3">
                  <span className="text-lg">🇧🇷</span>
                  <span className="text-sm text-muted-foreground">+55</span>
                </div>
                <MaskedInput
                  mask="(99) 99999-9999"
                  className="flex-1 h-11"
                  value={phoneInput}
                  onChange={setPhoneInput}
                  placeholder="(00) 00000-0000"
                  autoFocus
                />
              </div>

              <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800 dark:bg-blue-950/30 dark:text-blue-300">
                <Sparkles className="inline h-4 w-4 mr-1" />
                Após conectar, você poderá enviar áudios e fotos diretamente para o Obrim!
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => setStep('intro')}
                  className="flex-1"
                >
                  Voltar
                </Button>
                <Button 
                  onClick={handleVerify}
                  disabled={saving || phoneInput.replace(/\D/g, '').length < 10}
                  className="flex-1"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verificando...
                    </>
                  ) : (
                    <>
                      Verificar
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          </>
        )}

        {step === 'success' && (
          <>
            <DialogHeader className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
              <DialogTitle className="text-2xl text-emerald-600">
                WhatsApp Conectado!
              </DialogTitle>
              <DialogDescription className="text-base">
                Você já pode usar seu assistente de bolso
              </DialogDescription>
            </DialogHeader>

            <div className="mt-6 space-y-4">
              <div className="rounded-lg border bg-muted/30 p-4">
                <p className="font-medium mb-2">Experimente agora:</p>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Envie <Badge variant="secondary" className="mx-1">status</Badge> para ver suas obras</li>
                  <li>• Envie <Badge variant="secondary" className="mx-1">tarefas</Badge> para ver pendências</li>
                  <li>• Grave um <Badge variant="secondary" className="mx-1">áudio</Badge> para registrar atividades</li>
                </ul>
              </div>

              <Button onClick={handleClose} className="w-full">
                Começar a usar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
